'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

import type { HolidayImage } from '@/src/types/holidays'

import HeroCarousel from './heroCarousel'

interface HeroCarouselWrapperProps {
    images: HolidayImage[]
    onOpenAt: (index: number) => void
}

const HeroCarouselWrapper: React.FC<HeroCarouselWrapperProps> = ({ images, onOpenAt }) => {
    const t = useTranslations('holidaysPage')
    const scrollerRef = useRef<HTMLDivElement | null>(null)
    const frameRef = useRef<number | null>(null)
    const [activeIndex, setActiveIndex] = useState(0)

    /*
     * The active slide is tracked with an IntersectionObserver against the scroller rather than
     * from scroll offsets. It reports the slide that is actually on screen, needs no arithmetic
     * that RTL would invert, and keeps working where scroll events are throttled or coalesced.
     * The scroll handler below stays as a cheap backstop.
     */
    useEffect(() => {
        const scroller = scrollerRef.current
        if (!scroller || images.length < 2) return

        const slides = Array.from(scroller.children).filter((node): node is HTMLElement => node instanceof HTMLElement)
        if (!slides.length) return

        const observer = new IntersectionObserver(
            entries => {
                const best = entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
                if (!best) return
                const index = slides.indexOf(best.target as HTMLElement)
                if (index !== -1) setActiveIndex(current => (current === index ? current : index))
            },
            { root: scroller, threshold: [0.5, 0.75, 1] }
        )

        slides.forEach(slide => observer.observe(slide))
        return () => observer.disconnect()
    }, [images.length])

    const handleScroll = useCallback(() => {
        if (frameRef.current !== null) return
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null
            const el = scrollerRef.current
            if (!el || !el.offsetWidth) return
            const next = Math.round(Math.abs(el.scrollLeft) / el.offsetWidth)
            setActiveIndex(current => (current === next ? current : next))
        })
    }, [])

    if (!images.length) return null

    return (
        <HeroCarousel
            images={images}
            activeIndex={Math.min(activeIndex, images.length - 1)}
            openGalleryLabel={t('openGallery')}
            onScroll={handleScroll}
            onOpenAt={onOpenAt}
            scrollerRef={scrollerRef}
        />
    )
}

export { HeroCarouselWrapper }
export default HeroCarouselWrapper
